import type { IEntity } from '../IEntity';
import type { IDockPointer } from '../models/DockPointer';
import { TypeId } from '../models/TypeId';
import { EntityTypes } from '../schema/types';
import { Tab, type INewTabOpts, type ITab, type TabEnsureResult } from '../entities/tab';
import { ConnectionManager, type BroadcastMessage, type DataOpType } from '../websocket';
import { TabLifecycleRegistry } from './tab-lifecycle-registry';
import { ViewModeEvent, viewModeMemory } from './view-mode-memory';
import { computeReorder } from './tab-order';
import { resolveNextTabPure } from './tab-selection';
import {
  tabForDockKey,
  tabForTargetId,
  tabOwnsTarget,
  tabsForProject,
  terminalTabsForScope,
  type TabScope,
} from './tab-selectors';

type TabConnection = Pick<ConnectionManager, 'on' | 'off'>;

/** The low-level wire operations used by TabManager. The production gateway is
 * `Tab`; the seam exists so a manager can be tested without replacing global
 * SDK state. */
export interface TabGateway {
  listAll(): Promise<Tab[]>;
  newTab(pointer: string, options?: INewTabOpts): Promise<Tab[]>;
  getFromDockPointer(
    dock: IDockPointer,
    options?: { parentTabId?: string | null; afterTabId?: string | null },
  ): Promise<TabEnsureResult>;
  resolveDockTarget(dock: IDockPointer): ReturnType<typeof Tab.resolveDockTarget>;
  activateById(tabId: string): Promise<void>;
  closeById(tabId: string): Promise<Tab[]>;
  closeManyByIds(tabIds: string[], projectId?: string | null): Promise<Tab[]>;
  renameById(tabId: string, name: string): Promise<Tab[]>;
  setNameById(tabId: string, name: string): Promise<Tab[]>;
  reorder(
    tabId: string,
    afterId: string | null,
    beforeId: string | null,
    projectId?: string | null,
  ): Promise<Tab[]>;
}

export interface TabManagerOptions {
  gateway?: TabGateway;
  connection?: TabConnection | (() => TabConnection);
}

const defaultConnection = (): TabConnection => ConnectionManager.getInstance();

function coerceTab(tab: Tab | ITab): Tab {
  if (tab instanceof Tab) return tab;
  try {
    return new Tab(tab);
  } catch {
    // Constructing a second entity with an already-cached id can throw. Keep
    // the old store's data-only fallback so adopting a wire list stays total.
    const fallback = Object.create(Tab.prototype) as Tab;
    Object.assign(
      fallback,
      {
        id: tab.id ?? '',
        type: Tab.type,
        pointer: '',
        target_type: null,
        target_id: null,
        parent_tab_id: null,
        visible: true,
        icon_key: null,
        worktree: false,
        name: null,
        project_id: null,
        tab_order: 0,
        last_active_at: null,
        status: null,
        is_disabled: false,
      },
      tab,
    );
    return fallback;
  }
}

/**
 * Headless owner of the canonical, unscoped tab projection and tab-domain
 * operations. It deliberately owns no selected-tab state and performs no
 * navigation: selection remains URL-first in the application.
 */
export class TabManager {
  readonly lifecycle = new TabLifecycleRegistry();

  private readonly gateway: TabGateway;
  private readonly getConnection: () => TabConnection;
  private snapshot: Tab[] = [];
  private readonly listeners = new Set<() => void>();
  private refreshInFlight: Promise<Tab[]> | null = null;
  private refreshRequestedAgain = false;
  private attached = false;
  /** The initial load has been STARTED (it may still be in flight). */
  private loadedOnce = false;
  /** A list has been ADOPTED: the snapshot can answer navigation decisions. */
  private hydrated = false;
  private broadcastHandler: ((message: BroadcastMessage) => void) | null = null;
  private attachedConnection: TabConnection | null = null;
  private pendingIntentKey: string | null = null;

  constructor(options: TabManagerOptions = {}) {
    this.gateway = options.gateway ?? Tab;
    const connection = options.connection;
    this.getConnection =
      typeof connection === 'function'
        ? connection
        : connection
          ? () => connection
          : defaultConnection;
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getSnapshot = (): readonly Tab[] => this.snapshot;

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  private refreshInBackground(): void {
    // Explicit refresh callers receive failures. Event/startup refreshes own
    // them so a disconnected backend cannot create an unhandled rejection.
    void this.refresh().catch(() => undefined);
  }

  attachTabsChangedPing(): void {
    if (this.attached) return;
    const connection = this.getConnection();
    const handler = (message: BroadcastMessage): void => {
      if (message?.broadcast_type === 'tabs_changed') this.refreshInBackground();
    };
    connection.on('on_broadcast', handler);
    this.attached = true;
    this.attachedConnection = connection;
    this.broadcastHandler = handler;
  }

  ensureInitialLoad(): void {
    if (this.loadedOnce) return;
    this.loadedOnce = true;
    this.refreshInBackground();
  }

  start(): void {
    this.attachTabsChangedPing();
    this.ensureInitialLoad();
  }

  refresh(): Promise<Tab[]> {
    if (this.refreshInFlight) {
      this.refreshRequestedAgain = true;
      return this.refreshInFlight;
    }

    this.refreshInFlight = (async () => {
      try {
        let tabs = await this.gateway.listAll();
        this.adoptGlobal(tabs);
        while (this.refreshRequestedAgain) {
          this.refreshRequestedAgain = false;
          tabs = await this.gateway.listAll();
          this.adoptGlobal(tabs);
        }
        return tabs;
      } finally {
        this.refreshInFlight = null;
        this.refreshRequestedAgain = false;
      }
    })();
    return this.refreshInFlight;
  }

  /** Replace the canonical store with an unscoped `list_all` projection. */
  adoptGlobal(tabs: readonly (Tab | ITab)[]): void {
    this.snapshot = tabs.map(coerceTab);
    this.hydrated = true;
    // Lifecycle observers must settle before tab observers render this list.
    this.lifecycle.reconcile(this.snapshot);
    this.notify();
  }

  previewReorder(reorderId: string, afterId: string | null, beforeId: string | null): void {
    const byId = new Map(this.snapshot.map((tab) => [tab.id, tab]));
    const order = computeReorder(
      this.snapshot.map((tab) => tab.id),
      reorderId,
      afterId,
      beforeId,
    );
    this.snapshot = order.map((id) => byId.get(id)).filter((tab): tab is Tab => tab != null);
    this.notify();
  }



  forProject(projectId: string | null): Tab[] {
    return tabsForProject(this.snapshot, projectId);
  }




  findByDockKey(key: string | null | undefined): Tab | null {
    return tabForDockKey(this.snapshot, key);
  }





  setPendingIntent(key: string | null): void {
    this.pendingIntentKey = key;
  }

  peekPendingIntent(): string | null {
    return this.pendingIntentKey;
  }

  consumePendingIntent(): void {
    this.pendingIntentKey = null;
  }

  resolveNext(
    tabs: readonly Tab[],
    excludeIds?: ReadonlySet<string>,
    preferProjectId?: string | null,
  ): Tab | null {
    const result = resolveNextTabPure({
      tabs,
      excludeIds,
      preferProjectId,
      pendingIntentKey: this.pendingIntentKey,
    });
    if (result.consumedPendingIntent) this.consumePendingIntent();
    return result.tab;
  }

  listAll(): Promise<Tab[]> {
    return this.gateway.listAll();
  }

  /** A list has been adopted — an empty snapshot now means "no tabs", not "not loaded yet". */
  isHydrated(): boolean {
    return this.hydrated;
  }

  /**
   * The global tab list for a navigation decision: the in-memory snapshot once
   * it has been adopted at least once, a `list_all` round trip only before that.
   *
   * The snapshot is kept current by every write that returns a list and by the
   * `tabs_changed` broadcast, so re-listing on every click bought nothing but a
   * round trip on the switch path — the one place a switch is felt.
   */
  async snapshotOrRefresh(): Promise<readonly Tab[]> {
    return this.hydrated ? this.snapshot : this.refresh();
  }

  newTab(pointer: string, options?: INewTabOpts): Promise<Tab[]> {
    return this.gateway.newTab(pointer, options);
  }

  /** Get-or-create the dock's tab. `viewMode` is the mode the dock is shown in;
   *  a minted row reports it to view-mode memory as `TabCreate`. */
  async ensureDock(
    dock: IDockPointer,
    options: { parentTabId?: string | null; afterTabId?: string | null; viewMode?: string | null } = {},
  ): Promise<TabEnsureResult> {
    const { viewMode, ...placement } = options;
    const result = await this.gateway.getFromDockPointer(dock, placement);
    if (result.created) {
      const tab = tabForDockKey(result.tabs, dock.tabHash);
      if (tab) this.recordViewModeEvent(tab, ViewModeEvent.TabCreate, viewMode);
    }
    return result;
  }

  /** Report a tab event to view-mode memory, against the tab's target. */
  recordViewModeEvent(tab: Tab, event: ViewModeEvent, mode: string | null | undefined): void {
    if (!mode || !viewModeMemory.wants(event)) return;
    viewModeMemory.record(
      event,
      { tab: viewModeMemory.targetFor(this.typeIdOrNull(tab.target_type, tab.target_id)) },
      mode,
    );
  }

  resolveDockTarget(dock: IDockPointer): ReturnType<typeof Tab.resolveDockTarget> {
    return this.gateway.resolveDockTarget(dock);
  }

  activate(tabId: string): Promise<void> {
    this.stampRecencyLocally(tabId);
    return this.gateway.activateById(tabId);
  }

  /**
   * `activate` broadcasts no `tabs_changed`, so the snapshot would keep the old
   * stamp — and recency is what project entry resolves against (read on demand
   * via `resolveNext`). No `notify()`: that would re-render every tab
   * subscriber on every switch; the server's next list carries the same stamp.
   */
  private stampRecencyLocally(tabId: string): void {
    const tab = this.snapshot.find((t) => t.id === tabId);
    if (tab) tab.last_active_at = Date.now();
  }

  close(tabId: string): Promise<Tab[]> {
    return this.gateway.closeById(tabId);
  }

  closeMany(tabIds: string[], projectId?: string | null): Promise<Tab[]> {
    return this.gateway.closeManyByIds(tabIds, projectId);
  }

  rename(tabId: string, name: string): Promise<Tab[]> {
    return this.gateway.renameById(tabId, name);
  }

  setName(tabId: string, name: string): Promise<Tab[]> {
    return this.gateway.setNameById(tabId, name);
  }

  reorder(
    tabId: string,
    afterId: string | null,
    beforeId: string | null,
    projectId?: string | null,
  ): Promise<Tab[]> {
    return this.gateway.reorder(tabId, afterId, beforeId, projectId);
  }

  async getTerminalTabsSnapshot(scope: TabScope = 'all', projectId: string | null = null): Promise<Tab[]> {
    // The loaded list, like every switch-path read (`snapshotOrRefresh`): writes
    // and the `tabs_changed` broadcast keep it current, so re-listing here only
    // made the default-shell / fallback routes wait on a round trip every visit.
    const tabs = await this.snapshotOrRefresh();
    return terminalTabsForScope(tabs, scope, projectId);
  }

  async closeTarget(target: TypeId | string): Promise<void> {
    const tab = await this.resolveTargetFromStore(target);
    if (tab) await this.close(tab.id);
  }

  async renameTarget(target: TypeId | string, name: string): Promise<void> {
    const tab = await this.resolveTargetFromStore(target);
    if (tab) await this.rename(tab.id, name);
  }

  async syncTargetName(target: TypeId | string, name: string): Promise<void> {
    const tab = await this.resolveTargetFromStore(target);
    if (tab && tab.name !== name) await this.setName(tab.id, name);
  }

  async syncDockName(tabHash: string | null | undefined, name: string | null | undefined): Promise<void> {
    const trimmed = name?.trim();
    if (!tabHash || !trimmed) return;
    const tab = this.findByDockKey(tabHash);
    if (!tab || tab.name === trimmed) return;
    await this.setName(tab.id, trimmed);
    await this.refresh();
  }

  attachContentEntitySync(): () => void {
    const connection = this.getConnection();
    const handler = (_typeIdString: string, op: DataOpType, data: IEntity): void => {
      if (op === 'delete') return;
      const type = (data as { type?: string | null } | null)?.type;
      if (type === EntityTypes.Shell || type === EntityTypes.AgenticProcess) return;
      const id = data?.id;
      const name = (data as { name?: string | null } | null)?.name;
      const remote = (data as { remote?: unknown } | null)?.remote;
      if (!type || !id) return;
      const tab = this.snapshot.find((candidate) => tabOwnsTarget(candidate, id, type));
      if (!tab) return;
      const nameChanged = typeof name === 'string' && name.length > 0 && tab.name !== name;
      const remoteChanged = typeof remote === 'boolean' && tab.target_remote !== remote;
      if (!nameChanged && !remoteChanged) return;
      void (async () => {
        if (nameChanged) await this.setName(tab.id, name);
        await this.refresh();
      })().catch(() => undefined);
    };
    connection.on('on_data_op', handler);
    return () => connection.off('on_data_op', handler);
  }

  stampTargetRecency(targetType: string, targetId: string): void {
    const matches = (tab: Tab): boolean => tabOwnsTarget(tab, targetId, targetType);
    const cachedId = this.snapshot.find(matches)?.id;
    const resolvedId =
      cachedId !== undefined
        ? Promise.resolve(cachedId)
        : this.refresh().then((tabs) => tabs.find(matches)?.id);
    void resolvedId
      .then((id) => (id ? this.activate(id) : undefined))
      .catch(() => undefined);
  }

  resetForTests(): void {
    if (this.attachedConnection && this.broadcastHandler) {
      this.attachedConnection.off('on_broadcast', this.broadcastHandler);
    }
    this.snapshot = [];
    this.listeners.clear();
    this.refreshInFlight = null;
    this.refreshRequestedAgain = false;
    this.attached = false;
    this.loadedOnce = false;
    this.hydrated = false;
    this.broadcastHandler = null;
    this.attachedConnection = null;
    this.pendingIntentKey = null;
    this.lifecycle.resetForTests();
  }

  private typeIdOrNull(type: string | null | undefined, id: string | null | undefined): TypeId | null {
    if (!type || !id) return null;
    try {
      return new TypeId(type, id);
    } catch {
      return null;
    }
  }

  private parseTarget(target: TypeId | string): string | null {
    try {
      return new TypeId(typeof target === 'string' ? target : target.toString()).id;
    } catch {
      return null;
    }
  }

  private async resolveTargetFromStore(target: TypeId | string): Promise<Tab | null> {
    const targetId = this.parseTarget(target);
    if (!targetId) return null;
    let tabs = this.snapshot;
    if (tabs.length === 0) tabs = await this.refresh();
    return tabForTargetId(tabs, targetId);
  }
}

export const tabManager = new TabManager();
